import { useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { IoCaretDownSharp } from "react-icons/io5";
import styled, { css } from "styled-components";

import {
  DAVYS_GREY_100,
  DAVYS_GREY_600,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  ERROR_RED_500,
  MOSS_GREEN_100,
  MOSS_GREEN_900,
} from "./colors";
import FlexBox from "./FlexBox";
import { Body2, Support } from "./Headings";
import { Modal } from "./Modal";
import { FiChevronRight } from "react-icons/fi";

const DropdownWrapper = styled.div`
  width: ${({ width }) => width};
  display: ${({ hideDropdown }) => (hideDropdown ? "none" : "block")};
`;

const Input = styled(FlexBox)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${({ padding }) => padding};
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 0.5rem;
`;

const InputText = styled(Body2)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionList = styled(FlexBox)`
  height: 100%;
  overflow-y: auto;
`;

const Option = styled(FlexBox)`
  padding: 1rem;
  justify-content: space-between;
  align-items: center;
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${MOSS_GREEN_100};
      color: ${MOSS_GREEN_900};
    `}
`;

const Details = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  row-gap: 0.25rem;
`;

const MobileDisclaimer = styled.div`
  display: block;
  padding: 1rem 1.5rem;
  text-align: center;
  -webkit-text-align: center;
  background-color: ${DAVYS_GREY_100};
`;

const DropdownMobile = ({
  width = "100%",
  padding = "0.875rem",
  options,
  placeholder,
  value,
  onSelect,
  showBottomSheetOptions = false,
  color = DAVYS_GREY_800,
  borderColor = DAVYS_GREY_600,
  isMultiSelect = false,
  selectedOptions = [],
  dismissDropdown = () => {},
  dismissOnClickOutside = false,
  showChevron = false,
  disclaimerText = "",
  trackEvent,
  sheetId,
}) => {
  const [showOptions, setShowOptions] = useState(false);

  const toggleDropdown = () => {
    if (typeof trackEvent === "function" && !showOptions) {
      trackEvent();
    }
    if (isMultiSelect || dismissOnClickOutside) {
      dismissDropdown();
    } else {
      setShowOptions(!showOptions);
    }
  };

  const handleOptionClick = option => {
    let updatedOptions = [];
    if (isMultiSelect) {
      if (selectedOptions.includes(option)) {
        updatedOptions = selectedOptions.filter(item => item !== option);
      } else {
        updatedOptions = [...selectedOptions, option];
      }
    } else {
      toggleDropdown(); // safe now that ignoreId/sheetId shields the outer modal
    }
    onSelect(isMultiSelect ? updatedOptions : option);
  };

  return (
    <DropdownWrapper width={width} hideDropdown={showBottomSheetOptions}>
      <Input
        padding={padding}
        onClick={toggleDropdown}
        borderColor={borderColor}
      >
        <InputText bold={!!value} color={!value ? DAVYS_GREY_600 : color}>
          {!value ? placeholder : value?.label || value}
        </InputText>
        <IoCaretDownSharp color={color} />
      </Input>
      {(showOptions || showBottomSheetOptions) && (
        <Modal
          id={sheetId}
          borderRadius="1rem"
          mobileBorderRadius="1rem 1rem 0 0"
          mobileHeight="fit-content"
          isMobileBottomSheet={true}
          togglePopup={toggleDropdown}
        >
          <OptionList column width="100%" hei padding="1rem 0 0">
            {options?.map((option, index) => {
              if (!option) return null;
              const isSelected = isMultiSelect
                ? selectedOptions.includes(option)
                : value?.label
                ? value?.label === option?.label
                : value === option;
              const optionColor = option?.labelColor || DAVYS_GREY_800;

              const hasSubLabel = !!option?.subLabel;

              return (
                <Option
                  key={index}
                  isSelected={isSelected}
                  onClick={() => handleOptionClick(option)}
                  id={`session_options_mobile_${(option?.label || option).toLowerCase().replace(/\s+/g, '_')}`}
                >
                  <Details>
                    <Body2
                      bold={!hasSubLabel}
                      color={isSelected ? MOSS_GREEN_900 : optionColor}
                    >
                      {option?.name || option?.label || option}
                    </Body2>
                    {hasSubLabel && (
                      <Support
                        color={isSelected ? MOSS_GREEN_900 : DAVYS_GREY_700}
                      >
                        {option.subLabel}
                      </Support>
                    )}
                  </Details>
                  {isSelected && <FaCheck color={MOSS_GREEN_900} />}
                  {showChevron && <FiChevronRight color={optionColor} />}
                </Option>
              );
            })}
          </OptionList>
          {disclaimerText && (
            <MobileDisclaimer>
              <Support color={DAVYS_GREY_700}>{disclaimerText}</Support>
            </MobileDisclaimer>
          )}
        </Modal>
      )}
    </DropdownWrapper>
  );
};

export default DropdownMobile;
